import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import {
  DRIVER_DOCUMENTS,
  type DriverDocumentIssue,
  documentDateRuleLabelFr,
  driverDocumentIssueLabelFr,
  findDriverDocumentFile,
  getDriverDocumentDateField,
} from "@repo/types";
import { useSelector } from "@tanstack/react-store";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { Document } from "@/components/common/upload";
import { ReviewItem } from "@/components/common/upload";
import DRIVER_DOCS from "@/components/Delivery/utils/driver-docs-constants";
import type { GetDriverByIdQuery } from "@/libs/generated/operations";

import type { DriverFormType } from "./driver-form-simple";

type DriverType = GetDriverByIdQuery["getDriverById"];

type Props = {
  form: DriverFormType;
  driverData: DriverType;
};

const highlightSx = {
  scrollMarginTop: 96,
  "& .MuiListItem-root": {
    borderColor: "warning.main",
    animation: "document-highlight 1.2s ease-in-out 3",
  },
  "@keyframes document-highlight": {
    "0%, 100%": { borderColor: "var(--mui-palette-grey-200, #eee)" },
    "50%": { borderColor: "var(--mui-palette-warning-main, #ed6c02)" },
  },
};

export function DriverDocumentsSection({ form, driverData }: Props) {
  const docs = useSelector(form.store, (s) => s.values.docs) ?? [];
  // Set by document notifications (`?document=<name>`), see getAdminNotificationPath.
  const [searchParams] = useSearchParams();
  const highlighted = searchParams.get("document");
  const highlightedName = highlighted ? findDriverDocumentFile(highlighted)?.name : undefined;
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightedName)
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedName]);

  // GetDriverFormDefaultValues builds `docs` from DRIVER_DOCS, so indexes align.
  const updateDoc = (index: number, updated: Document) => {
    const current = (form.getFieldValue("docs") as Document[] | undefined) ?? [];
    const next = current.map((d, i) => (i === index ? (updated as never) : d));
    form.setFieldValue("docs" as never, next as never);
  };

  const documentIssues = (driverData?.documentIssues ?? []) as DriverDocumentIssue[];
  const severeKinds = new Set(["EXPIRED", "MISSING", "REJECTED"]);
  const severity = documentIssues.some((issue) => severeKinds.has(issue.kind))
    ? "error"
    : "warning";
  const driverIssues = documentIssues.filter((issue) => issue.actor === "DRIVER");
  const adminIssues = documentIssues.filter((issue) => issue.actor === "ADMIN");

  return (
    <Grid size={{ xs: 12 }}>
      <Typography variant="h6" gutterBottom>
        Documents
      </Typography>
      {documentIssues.length > 0 ? (
        <Alert severity={severity} sx={{ mb: 2 }}>
          {driverIssues.length > 0 ? (
            <Stack spacing={0.25} sx={{ mb: adminIssues.length > 0 ? 1 : 0 }}>
              <Typography variant="subtitle2">À faire par le chauffeur</Typography>
              {driverIssues.map((issue) => (
                <Typography key={`${issue.document}-${issue.kind}`} variant="body2">
                  {driverDocumentIssueLabelFr(issue)}
                </Typography>
              ))}
            </Stack>
          ) : null}
          {adminIssues.length > 0 ? (
            <Stack spacing={0.25}>
              <Typography variant="subtitle2">À faire par un admin</Typography>
              {adminIssues.map((issue) => (
                <Typography key={`${issue.document}-${issue.kind}`} variant="body2">
                  {driverDocumentIssueLabelFr(issue)}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Alert>
      ) : null}
      <Stack spacing={3} sx={{ bgcolor: "#E7E6F7", borderRadius: 2, p: 2 }}>
        {DRIVER_DOCUMENTS.map((document) => {
          const dateField = getDriverDocumentDateField(document);
          return (
            <Stack key={document.id} spacing={2}>
              <Stack sx={{ px: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {document.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dateField ? documentDateRuleLabelFr(dateField) : "Sans expiration"}
                </Typography>
              </Stack>
              {document.files.map((file) => {
                const index = DRIVER_DOCS.findIndex((slot) => slot.id === file.name);
                const slot = DRIVER_DOCS[index];
                const formValue = docs[index] as Document | undefined;
                if (!slot || !formValue) return null;
                const isHighlighted = file.name === highlightedName;
                return (
                  <Box
                    key={file.name}
                    ref={isHighlighted ? highlightRef : undefined}
                    data-highlighted={isHighlighted || undefined}
                    data-document-name={file.name}
                    sx={isHighlighted ? highlightSx : undefined}
                  >
                    <ReviewItem
                      value={formValue}
                      meta={{
                        folder: `driver-application/${driverData?.email}/${file.name}`,
                        subFolder: driverData?.email ?? "",
                        driverId: driverData?.id,
                      }}
                      handleFileUploaded={(updated) => updateDoc(index, updated)}
                      handleDelete={() =>
                        updateDoc(index, {
                          ...slot,
                          key: "",
                          url: "",
                          metadata: { id: slot.id },
                        } as Document)
                      }
                    />
                  </Box>
                );
              })}
            </Stack>
          );
        })}
      </Stack>
    </Grid>
  );
}
